<!-- eslint-disable vue/no-v-html -->
<template>
    <lsn-sider :is-open="isOpen" class="sl-review-request-comments">
        <div class="flex flex-col h-full w-full">
            <!-- Sidebar title -->
            <div class="flex justify-between items-center mb-6">
                <div class="sl-review-request-comments__title" v-html="title"></div>

                <mdi-icon
                    icon="mdiClose"
                    class="sl-review-request-comments__close"
                    @click="close"
                />
            </div>

            <!-- Reason mentioned by the customer -->
            <div class="sl-review-request-comments__reason">
                <div class="absolute -top-2 left-4 px-2 bg-white text-primary-500">
                    {{ t('review_requests.reason.label') }}
                </div>

                <div class="italic text-gray-600 leading-tight whitespace-pre-line wrap-break-word">
                    {{ reason }}
                </div>
            </div>

            <!-- Comments list -->
            <perfect-scrollbar
                ref="commentsList"
                class="sl-review-request-comments__list"
            >
                <div v-if="fetching" class="text-center">
                    <img src="/img/loader-5.gif" alt="loading" class="inline" />
                </div>

                <template v-else>
                    <div
                        v-if="fetchingPrevious"
                        class="mb-2 text-center"
                    >
                        <img
                            src="/img/loader.gif"
                            alt="loading…"
                            class="inline"
                        />
                    </div>

                    <div v-else>
                        <button
                            v-if="previousOrderReference"
                            class="mb-2 w-full text-primary-500 text-center text-sm hover:underline"
                            @click="fetchPreviousComments"
                        >
                            {{ t('review_requests.load_previous_comments.label') }}
                        </button>
                    </div>

                    <div
                        v-if="comments.length + previousComments.size > 0"
                        class="flex flex-col grow"
                    >
                        <transition-group name="slide-right">
                            <!-- Previous Comments -->
                            <template v-for="[reference, prevComments] in previousComments" :key="reference">
                                <div class="sl-review-request-comments__separator">
                                    <div>{{ reference }}</div>
                                </div>

                                <div
                                    v-for="comment in prevComments"
                                    :key="comment.id"
                                    class="sl-review-request-comments__comment"
                                    :class="getCommentClass(comment)"
                                >
                                    <div class="flex justify-between mb-2 text-sm">
                                        <!-- Author -->
                                        <div class="font-bold">
                                            {{ comment.user.first_name }}
                                            {{ comment.user.last_name }}
                                        </div>

                                        <!-- Date & Time -->
                                        <div
                                            class="flex justify-end items-center gap-1 h-[20px]"
                                            :class="{
                                                'group-hover:hidden': (!editing || editing === comment.id) &&
                                                    comment.user.username === cUsername,
                                                'text-primary-400': comment.user.username === cUsername,
                                                'text-gray-400': comment.user.username !== cUsername,
                                            }"
                                        >
                                            <mdi-icon
                                                v-if="comment.updated_at !== comment.created_at"
                                                v-tippy
                                                :content="t('common.edited') + ' ' +
                                                    moment(comment.updated_at).format('DD.MM.YY HH:mm')"
                                                icon="mdiInformationOutline"
                                                class="cursor-help w-4 h-4 text-gray-400"
                                            />

                                            <span>{{ moment(comment.created_at).format('DD.MM.YY HH:mm') }}</span>
                                        </div>
                                    </div>

                                    <!-- Content -->
                                    <div class="leading-tight whitespace-pre-line wrap-break-word">
                                        {{ comment.content }}
                                    </div>
                                </div>
                            </template>

                            <div
                                v-if="sortedPreviousComments.size"
                                class="sl-review-request-comments__separator"
                            >
                                <div>{{ request.order.reference }}</div>
                            </div>

                            <!-- Comments -->
                            <div
                                v-for="comment in comments"
                                :key="comment.id"
                                class="sl-review-request-comments__comment group"
                                :class="getCommentClass(comment)"
                            >
                                <div class="flex justify-between mb-2 text-sm">
                                    <!-- Author -->
                                    <div class="font-bold">
                                        {{ comment.user.first_name }}
                                        {{ comment.user.last_name }}
                                    </div>

                                    <!-- Date & Time -->
                                    <div
                                        class="flex justify-end items-center gap-1 h-[20px]"
                                        :class="{
                                            'group-hover:hidden': (!editing || editing === comment.id) &&
                                                comment.user.username === cUsername,
                                            'text-primary-400': comment.user.username === cUsername,
                                            'text-gray-400': comment.user.username !== cUsername,
                                        }"
                                    >
                                        <mdi-icon
                                            v-if="comment.updated_at !== comment.created_at"
                                            v-tippy
                                            :content="t('common.edited') + ' ' +
                                                moment(comment.updated_at).format('DD.MM.YY HH:mm')"
                                            icon="mdiInformationOutline"
                                            class="cursor-help w-4 h-4 text-gray-400"
                                        />

                                        <span>{{ moment(comment.created_at).format('DD.MM.YY HH:mm') }}</span>
                                    </div>

                                    <!-- Actions -->
                                    <div
                                        v-if="!readOnly"
                                        class="hidden justify-end items-center gap-1 h-[20px] text-primary-500"
                                        :class="{
                                            'group-hover:flex': (!editing || editing === comment.id) &&
                                                comment.user.username === cUsername,
                                        }"
                                    >
                                        <!-- Edit button -->
                                        <button
                                            v-if="!editing && comment.user.username === cUsername"
                                            class="hover:text-primary-700"
                                            @click="editBtn_onClick(comment)"
                                        >
                                            <mdi-icon
                                                icon="mdiPencil"
                                                class="w-4 h-4"
                                            />
                                        </button>

                                        <button
                                            v-else-if="editing && editing === comment.id"
                                            class="hover:text-primary-700"
                                            @click="stopEditing"
                                        >
                                            <mdi-icon
                                                icon="mdiClose"
                                                class="w-4 h-4"
                                            />
                                        </button>

                                        <!-- Private/Public button -->
                                        <button
                                            v-if="isStaff &&
                                                !editing &&
                                                comment.user.username === cUsername"
                                            v-tippy
                                            :content="comment.private
                                                ? t('review_requests.comment.make_public.help')
                                                : t('review_requests.comment.make_private.help')"
                                            :class="comment.private
                                                ? 'hover:text-red-600'
                                                : 'hover:text-primary-700'"
                                            @click="editComment(comment.id, '', !comment.private)"
                                        >
                                            <img
                                                v-if="sending"
                                                src="/img/loader.gif"
                                                alt="..."
                                            />

                                            <mdi-icon
                                                v-else
                                                :icon="comment.private
                                                    ? 'mdiEyeOff'
                                                    : 'mdiEye'"
                                                class="w-4 h-4"
                                            />
                                        </button>

                                        <!-- Delete button -->
                                        <button
                                            v-if="!editing && comment.user.username === cUsername"
                                            class="hover:text-red-500"
                                            :class="deleting ? 'cursor-not-allowed' : 'cursor-pointer'"
                                            @click="deleteBtn_onClick(comment)"
                                        >
                                            <img
                                                v-if="deleting"
                                                src="/img/loader.gif"
                                                alt="…"
                                            />

                                            <mdi-icon
                                                v-else
                                                icon="mdiDeleteOutline"
                                                class="w-4 h-4"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <!-- Content -->
                                <div class="leading-tight whitespace-pre-line wrap-break-word">
                                    {{ comment.content }}
                                </div>
                            </div>
                        </transition-group>
                    </div>

                    <div v-else class="p-6 text-center text-gray-400">
                        {{ t('review_requests.no_comments') }}
                    </div>
                </template>
            </perfect-scrollbar>

            <!-- Actions -->
            <div class="sl-review-request-comments__actions">
                <!-- File uploader -->
                <div
                    class="cursor-pointer flex items-center gap-1 mb-2 text-primary-500 text-xl font-thin"
                    @click="showFiles = !showFiles"
                >
                    <div>
                        {{ t('review_requests.files.title') }}
                    </div>

                    <mdi-icon
                        icon="mdiChevronRight"
                        class="w-5 h-5"
                        :class="{ 'rotate-90': showFiles }"
                    />
                </div>

                <div v-if="showFiles" class="mb-4">
                    <file-uploader
                        class="mb-4"
                        :eid="request.entity.id"
                        :fitting-session-id="request.order.fitting_session_id"
                        :read-only="readOnly"
                        :typeless="typelessFiles"
                    />
                </div>

                <!-- Add/Edit comment form -->
                <template v-if="!readOnly">
                    <div class="mb-2 text-primary-500 text-xl font-thin">
                        {{ t('review_requests.add_comment.title') }}
                    </div>

                    <div class="flex justify-between items-end gap-2">
                        <!-- Content input -->
                        <div class="relative grow">
                            <textarea
                                ref="messageField"
                                v-model="message"
                                rows="1"
                                class="block w-full resize-none rounded border border-gray-400 px-2 py-1 text-base
                                text-gray-600 leading-tight outline-none
                                focus-visible:ring-1 focus-visible:ring-primary-500 focus-visible:ring-offset-1
                                disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100
                                disabled:text-gray-400"
                                :placeholder="t('review_requests.add_comment.placeholder')"
                                :disabled="sending"
                                @keydown.enter.meta.prevent="sendBtn_onClick"
                                @keydown.enter.ctrl.prevent="sendBtn_onClick"
                                @keydown.escape="stopEditing"
                            ></textarea>

                            <!-- Keyboard shortcut hint -->
                            <div class="absolute top-full right-0 mt-0.5 text-xs text-gray-500">
                                {{
                                    IS_MAC_OS
                                        ? t('review_requests.add_comment.send_hint_mac')
                                        : t('review_requests.add_comment.send_hint')
                                }}
                            </div>
                        </div>

                        <!-- Public/Private toggle -->
                        <button
                            v-if="isStaff"
                            v-tippy="{ placement: 'top' }"
                            :content="isPrivate
                                ? t('review_requests.add_comment.private.help')
                                : t('review_requests.add_comment.public.help')"
                            class="flex justify-center items-center rounded border w-8 h-8
                            bg-white-500 transition outline-none
                            focus-visible:ring-1 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
                            :class="isPrivate
                                ? 'border-gray-400 text-gray-400'
                                : 'border-red-600 text-red-600'
                            "
                            @click="isPrivate = !isPrivate"
                        >
                            <mdi-icon
                                :icon="isPrivate ? 'mdiEyeOff' : 'mdiEye'"
                                class="w-4 h-4"
                            />
                        </button>

                        <!-- Send button -->
                        <button
                            class="flex justify-center items-center rounded w-8 h-8
                            bg-primary-500 text-white transition outline-none
                            hover:bg-primary-300
                            focus:bg-primary-300
                            focus-visible:ring-1 focus-visible:ring-primary-500 focus-visible:ring-offset-1
                            active:bg-primary-600"
                            @click="sendBtn_onClick"
                        >
                            <img v-if="sending" src="/img/loader.gif" alt="..." />

                            <mdi-icon
                                v-else
                                :icon="editing ? 'mdiCheck' : 'mdiSend'"
                                class="w-4 h-4"
                            />
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </lsn-sider>
</template>

<script setup>
/**
 * @typedef {{
 *     id:         number,
 *     username:   string,
 *     first_name: ?string,
 *     last_name:  ?string,
 *     email:      string,
 *     visa:       ?string,
 * }} ReviewCommentUser
 *
 * @typedef {{
 * id:                number,
 * private:           boolean,
 * content:           string,
 * review_request_id: number,
 * user_id:           number,
 * user:              ReviewCommentUser,
 * created_at:        string,
 * updated_at:        string,
 * deleted_at:        ?string,
 * }} ReviewComment
 */

import axios from '@/axios';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import Order from '@/models/Order';
import ReviewRequest from '@/models/ReviewRequest';
import { getUserConfigurations } from '@/vue-configurations';
import ReviewComment from '@/models/ReviewComment';
import FileUploader from '@/components/FileUploader.vue';

// ------------------------------------------------------------ CONSTANTS

/** Maximum height (px) the message field can grow to before it starts scrolling. */
const MAX_MESSAGE_FIELD_HEIGHT = 250;

/** True on macOS, where the send shortcut uses Cmd rather than Ctrl. */
const IS_MAC_OS = /Mac|iPhone|iPad|iPod/.test(
    navigator.userAgentData?.platform ?? navigator.platform ?? ''
);

// ------------------------------------------------------------ PROPS & EMITS

const props = defineProps({
    isOpen:
    {
        type:     Boolean,
        required: true,
    },

    request:
    {
        type:     Object,
        required: true,
    },

    readOnly:
    {
        type:    Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n();
const store = useStore();
const toast = useToast();
const uconfig = getUserConfigurations();

// ------------------------------------------------------------ REFS

const commentsList = ref(null);
const messageField = ref(null);

// ------------------------------------------------------------ DATA

const initializing = ref(false);
const fetching = ref(false);
const fetchingPrevious = ref(false);
const sending = ref(false);
const editing = ref(0);
const deleting = ref(false);
const showFiles = ref(false);

/** @type {import("vue").Ref<ReviewComment[]>} */
const comments = ref([]);
const message = ref('');
const isPrivate = ref(false);
/** @type {import("vue").Ref<?number>} */
const previousOrderReference = ref(null);
/** @type {import("vue").Ref<Map<number, ReviewComment[]>>} */
const previousComments = ref(new Map());

// ------------------------------------------------------------ COMPUTED

const title = computed(() =>
{
    const o = props.request.order;
    return `${o.reference} &middot; ${o.article_data.PATIENT_REFERENCE1}`;
});
const cUsername = computed(() => store.state.account.cUser.username);
const reason = computed(() => props.request.order.article_data.LENS_COMMENT);
const isStaff = computed(() => store.getters['account/isStaff']);
const sortedPreviousComments = computed(() => new Map(Array.from(previousComments.value).reverse()));
const typelessFiles = computed(() =>
{
    const requestPurpose = props.request.order.article_data.LENS_COMMENT_PURPOSE;
    return requestPurpose === 'OTHER';
});

// ------------------------------------------------------------ METHODS

async function initialize()
{
    if(initializing.value)
    {
        return;
    }

    initializing.value = true;

    // Reset state
    editing.value = 0;
    message.value = '';
    previousOrderReference.value = props.request?.order.previous_order_reference;

    await nextTick();
    messageField.value?.focus();

    await fetchComments();

    initializing.value = false;
}

function close()
{
    emit('close');
}

async function fetchComments()
{
    // todo: add abort signal to request
    fetching.value = true;
    comments.value = [];
    previousComments.value = new Map();

    await scrollToTop();

    try
    {
        comments.value = await ReviewComment.fetchByRequest(props.request.id);
        return comments.value;
    }
    catch(error)
    {
        toast.error(t('common.errors.error'));
        throw error;
    }
    finally
    {
        fetching.value = false;

        // Scroll to the top/bottom
        if(comments.value.length)
        {
            // Comments => scroll to bottom
            await scrollToBottom();
        }
    }
}

/**
 * Retrieve a single order.
 *
 * @async
 * @param {number} reference
 * @returns {Promise<Order>}
 */
async function fetchOrder(reference)
{
    const url = `/api/order/reference/${reference}`;
    const response = await axios.get(url);
    return response.data;
}

/**
 * Iteratively load the comments as long as there is a previous order.
 *
 * @returns {void}
 */
async function fetchPreviousComments()
{
    if(fetching.value || fetchingPrevious.value)
    {
        return;
    }

    fetchingPrevious.value = true;

    try
    {
        while(previousOrderReference.value)
        {
            // Use previousOrderReference to fetch the previous order.
            const order = await fetchOrder(previousOrderReference.value);

            // Fetch the matching comments.
            const comms = await ReviewComment.fetchByOrder(order.reference);

            // Store those comments in the previousComments map (keyed by order reference).
            previousComments.value.set(order.reference, comms);

            // Update previousOrderReference.
            previousOrderReference.value = order.previous_order_reference;
        }
    }
    catch(error)
    {
        const status = error?.response?.status ?? 500;
        if(status === 404)
        {
            toast.error(t('common.errors.http.not_found'));
            return;
        }

        if(status >= 400 && status <= 499)
        {
            toast.error(t('common.errors.http.bad_request'));
            return;
        }

        if(status === 500)
        {
            toast.error(t('common.errors.http.internal_server_error'));
            return;
        }

        toast.error(error.message);
    }
    finally
    {
        fetchingPrevious.value = false;
    }
}

/**
 * Generate the list of CSS classes for a specific comment.
 *
 * @param {ReviewComment} comment
 * @returns {string[]}
 */
function getCommentClass(comment)
{
    const classes = [];

    if(comment.user.username === cUsername.value)
    {
        classes.push('sl-review-request-comments__comment--own');
    }
    else
    {
        classes.push('sl-review-request-comments__comment--other');
    }

    if(comment.private)
    {
        classes.push('sl-review-request-comments__comment--private');
    }

    if(editing.value)
    {
        if(editing.value === comment.id)
        {
            classes.push('sl-review-request-comments__comment--editing');
        }
        else
        {
            classes.push('sl-review-request-comments__comment--muted');
        }
    }

    return classes;
}

/**
 * Grow or shrink the message field to fit its content, up to a maximum height.
 *
 * @returns {void}
 */
function resizeMessageField()
{
    const el = messageField.value;
    if(!el)
    {
        return;
    }

    el.style.height = 'auto';

    // `scrollHeight` excludes the borders, which `box-sizing: border-box` includes.
    const borders = el.offsetHeight - el.clientHeight;
    const contentHeight = el.scrollHeight + borders;

    el.style.height = `${Math.min(contentHeight, MAX_MESSAGE_FIELD_HEIGHT)}px`;
    el.style.overflowY = contentHeight > MAX_MESSAGE_FIELD_HEIGHT ? 'auto' : 'hidden';
}

async function scrollToTop()
{
    await nextTick();
    const el = commentsList.value?.$el || commentsList.value?.container;
    if(el)
    {
        el.scrollTop = 0;
    }
}

async function scrollToBottom()
{
    await nextTick();
    const el = commentsList.value?.$el || commentsList.value?.container;
    if(el)
    {
        el.scrollTop = el.scrollHeight;
    }
}

async function sendBtn_onClick()
{
    if(sending.value)
    {
        return;
    }

    // Ensure content isn't empty
    const content = message.value?.trim() ?? '';
    if(content === '')
    {
        messageField.value?.focus();
        return;
    }

    // Staff members can choose to make their comment private
    const priv = isStaff.value ? isPrivate.value : null;

    if(editing.value)
    {
        await editComment(editing.value, content, priv);
    }
    else
    {
        await addComment(content, priv);
    }
}

/**
 * Create a new comment.
 *
 * @param {string}   content
 * @param {?boolean} isPrivate
 * @returns {Promise<ReviewComment>}
 */
function addComment(content, isPrivate)
{
    return new Promise((resolve, reject) =>
    {
        sending.value = true;

        const requestId = props.request.id;
        const url = '/api/review-request/{requestId}/comment'
            .replace('{requestId}', requestId);
        const data = { content };
        if(isPrivate !== null)
        {
            data.private = isPrivate;
        }

        axios.post(url, data)
            .then(async({ data: comment }) =>
            {
                // Add the new comment to the list
                comments.value.push(comment);
                resolve(comment);

                // Reset the message field
                message.value = '';

                // Scroll to the bottom
                await scrollToBottom();

                // Record last read time
                const now = Math.floor(new Date().getTime() / 1000);
                uconfig.set(`review_comments.${requestId}.last_read`, now);
            })
            .catch(error =>
            {
                reject(error);

                const status = error?.response?.status ?? 500;
                if(status === 404)
                {
                    toast.error(t('common.errors.http.not_found'));
                    return;
                }

                if(status >= 400 && status <= 499)
                {
                    toast.error(t('common.errors.http.bad_request'));
                    return;
                }

                if(status === 500)
                {
                    toast.error(t('common.errors.http.internal_server_error'));
                    return;
                }

                toast.error(error.message);
            })
            .then(() =>
            {
                sending.value = false;
            });
    });
}

/**
 * Update an existing comment.
 *
 * @param {number}   id
 * @param {string}   content
 * @param {?boolean} isPrivate
 * @returns {Promise<ReviewComment>}
 */
function editComment(id, content, isPrivate)
{
    return new Promise((resolve, reject) =>
    {
        sending.value = true;

        const url = '/api/review-request/{requestId}/comment/{commentId}'
            .replace('{requestId}', props.request.id)
            .replace('{commentId}', id);
        const data = {};
        if(content !== '')
        {
            data.content = content;
        }
        if(isPrivate !== null)
        {
            data.private = isPrivate;
        }

        axios.patch(url, data)
            .then(({ data: comment }) =>
            {
                // Replace the patched comment in the list
                const index = comments.value.findIndex(c => c.id === comment.id);
                if(index !== -1)
                {
                    comments.value.splice(index, 1, comment);
                }

                resolve(comment);

                // Reset the message field
                message.value = '';
                editing.value = 0;
            })
            .catch(error =>
            {
                reject(error);

                const status = error?.response?.status ?? 500;
                if(status === 404)
                {
                    toast.error(t('common.errors.http.not_found'));
                    return;
                }

                if(status >= 400 && status <= 499)
                {
                    toast.error(t('common.errors.http.bad_request'));
                    return;
                }

                if(status === 500)
                {
                    toast.error(t('common.errors.http.internal_server_error'));
                    return;
                }

                toast.error(error.message);
            })
            .then(() =>
            {
                sending.value = false;
            });
    });
}

async function editBtn_onClick(comment)
{
    if(editing.value)
    {
        return;
    }

    editing.value = comment.id;
    message.value = comment.content;
    isPrivate.value = comment.private;

    await nextTick();
    messageField.value?.focus();
}

function stopEditing()
{
    if(!editing.value)
    {
        return;
    }

    editing.value = 0;
    message.value = '';
}

function deleteBtn_onClick(comment)
{
    return new Promise((resolve, reject) =>
    {
        if(deleting.value)
        {
            return;
        }

        deleting.value = true;

        const url = '/api/review-request/{requestId}/comment/{commentId}'
            .replace('{requestId}', props.request.id)
            .replace('{commentId}', comment.id);
        axios.delete(url)
            .then(() =>
            {
                const index = comments.value.findIndex(c => c.id === comment.id);
                if(index !== -1)
                {
                    comments.value.splice(index, 1);
                }

                resolve();
            })
            .catch(error =>
            {
                reject(error);
                toast.error(t('common.errors.error'));
            })
            .then(() =>
            {
                deleting.value = false;
            });
    });
}

function updateLastRead(requestId)
{
    if(typeof requestId !== 'number' || parseInt(requestId) !== requestId)
    {
        throw new TypeError(`requestId must be an integer; got ${requestId}`);
    }

    const key = `review_comments.${requestId}.last_read`;
    const now = Math.floor(new Date().getTime() / 1000);
    uconfig.set(key, now);
}

// ------------------------------------------------------------ WATCHERS

watch(() => props.isOpen, async() =>
{
    if(props.isOpen)
    {
        await initialize();
        updateLastRead(props.request?.id);
    }
});
watch(() => props.request, initialize);
watch(message, async() =>
{
    await nextTick();
    resizeMessageField();
});
watch(sending, async sending =>
{
    if(sending)
    {
        return;
    }

    await nextTick();
    messageField.value?.focus();
});
</script>

<style scoped>
@reference "#main.css";

.sl-review-request-comments
{
    @apply w-[420px] p-8 pb-3 bg-white text-gray-500;
}

.sl-review-request-comments__title
{
    @apply text-primary-500 text-2xl font-thin;
}

.sl-review-request-comments__close
{
    @apply cursor-pointer w-8 h-8 transition
        hover:text-primary-500;
}

.sl-review-request-comments__reason
{
    @apply relative rounded-lg border border-primary-400 p-6 mb-6;
}

.sl-review-request-comments__list
{
    @apply flex flex-col grow rounded-lg mb-6;
}

.sl-review-request-comments__separator
{
    @apply relative mt-10 mb-4 w-full h-[1rem] bg-white;

    &:first-child
    {
        @apply mt-0;
    }

    & > div
    {
        @apply absolute top-0 bottom-0 left-[35%] z-10
            flex justify-center items-center mx-auto px-4 bg-white;
    }

    &::after
    {
        content: "";
        @apply absolute top-[0.5rem] block border-b border-gray-300 w-full;
    }
}

.sl-review-request-comments__comment
{
    @apply mb-2 rounded-lg px-6 py-4;
}

.sl-review-request-comments__comment--own
{
    @apply ml-10 bg-primary-100 text-primary-600;
}

.sl-review-request-comments__comment--other
{
    @apply mr-10 bg-gray-100 text-gray-600;
}

.sl-review-request-comments__comment--private
{
    @apply bg-yellow-200;
}

.sl-review-request-comments__comment--editing
{
    @apply ring-1 ring-inset ring-primary-500;
}

.sl-review-request-comments__comment--muted
{
    @apply opacity-30;
}

.sl-review-request-comments__actions
{
    @apply pb-5;
}
</style>
